/**
 * Admin Layout - Master Admin Dashboard Layout
 * Used by ServiceHive platform admins
 */

import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Sun,
  Moon,
  Bell,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import { useTheme } from "../../context/ThemeContext";

const adminNavItems = [
  { name: "Overview", icon: LayoutDashboard, path: "/admin" },
  { name: "Tenants", icon: Building2, path: "/admin/tenants" },
  { name: "Users", icon: Users, path: "/admin/users" },
  { name: "Billing Overview", icon: CreditCard, path: "/admin/billing" },
  { name: "Billing Config", icon: Settings, path: "/admin/billing-config" },
  { name: "Analytics", icon: BarChart3, path: "/admin/analytics" },
];

export default function AdminLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [clientCollapsed, setClientCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { currentTenant, tenants, switchTenant, canSwitchTenants } =
    useTenant();
  const { darkMode, toggleDarkMode, setRouteState } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleSwitchToClientView = () => {
    if (currentTenant) {
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
      {/* Admin Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen bg-gray-900 flex flex-col z-40 transition-all duration-300 ${sidebarCollapsed ? "w-20" : "w-64"}`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            {!sidebarCollapsed && (
              <div>
                <span className="font-semibold text-xl text-white">
                  ServiceHive
                </span>
                <span className="block text-xs text-purple-400">
                  Admin Console
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center text-gray-400 hover:text-white"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 overflow-y-auto">
          <ul className="space-y-1">
            {adminNavItems.map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.path}
                  end={item.path === "/admin"}
                  title={sidebarCollapsed ? item.name : undefined}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-purple-600 text-white shadow-lg shadow-purple-900/50"
                        : "text-gray-400 hover:bg-gray-800 hover:text-white"
                    } ${sidebarCollapsed ? "justify-center" : ""}`
                  }
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>{item.name}</span>}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Switch to Client View */}
          {canSwitchTenants && (
            <div className="mt-6 pt-6 border-t border-gray-800">
              <button
                onClick={() => setClientCollapsed((p) => !p)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-blue-400 hover:bg-gray-800 transition-colors ${sidebarCollapsed ? "justify-center" : ""}`}
              >
                <Building2 className="w-5 h-5" />
                {!sidebarCollapsed && <span>Client Dashboard</span>}
              </button>
              {clientCollapsed
                ? [
                    {
                      name: "Hotel Dashboard",
                      onClick: () => {
                        setRouteState("hotel");
                        handleSwitchToClientView();
                      },
                    },
                    // {
                    //   name: "Retail Dashboard",
                    //   onClick: () => {
                    //     setRouteState("retail");
                    //     handleSwitchToClientView();
                    //   },
                    // },
                    // {
                    //   name: "Restaurant Dashboard",
                    //   onClick: () => {
                    //     setRouteState("restaurant");
                    //     handleSwitchToClientView();
                    //   },
                    // },
                    // {
                    //   name: "Ecommerce Dashboard",
                    //   onClick: () => {
                    //     setRouteState("ecommerce");
                    //     handleSwitchToClientView();
                    //   },
                    // },
                  ].map((d, i) => (
                    <button
                      key={i}
                      onClick={d?.onClick}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-blue-400 hover:bg-gray-800 transition-colors ${sidebarCollapsed ? "justify-center" : ""}`}
                    >
                      <span>{d?.name}</span>
                    </button>
                  ))
                : null}
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-gray-800">
          <div
            className={`flex items-center gap-3 ${sidebarCollapsed ? "justify-center" : ""}`}
          >
            <div className="w-9 h-9 bg-purple-600 rounded-full flex items-center justify-center text-white font-medium">
              {user?.name?.[0] || "A"}
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name || "Admin"}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {user?.role || "Super Admin"}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className={`mt-3 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-900/30 transition-colors ${sidebarCollapsed ? "justify-center" : ""}`}
          >
            <LogOut className="w-4 h-4" />
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${sidebarCollapsed ? "ml-20" : "ml-64"}`}
      >
        {/* Top Bar */}
        <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-800 dark:text-white">
              Admin Console
            </h1>
            <div className="flex items-center gap-4">
              <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white">
                <Bell className="w-5 h-5" />
              </button>
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
              >
                {darkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
